import { useCanvasStore } from '../store/canvas-store';
import { emptyState } from '../store/commands';
import { decodeDoc, encodeDoc } from '../io/serialize';
import { broadcastDoc } from './doc-source';
import { useCurrentProject } from '../projects/currentProject';

const KEY = 'codeframe.doc.v2';
const LEGACY_KEY = 'codeframe.doc.v1';
let timer: number | undefined;

// Raised while we apply a doc programmatically (project open / remote adopt) so
// the autosave subscribers ignore the synthetic store change and don't push it
// back as a "user edit" (which would spuriously bump the server version).
let applyingDoc = false;
export function isApplyingDoc(): boolean { return applyingDoc; }

/** Initial boot: no project selected yet → restore the legacy local doc (or an
 *  empty canvas) so something renders behind the Projects overlay. First-run
 *  onboarding is no longer seeded here — every user gets a persisted Tutorial
 *  project instead (server-seeded), which avoids the seed-then-project-load flash.
 *  Project open replaces this via the sync controller / synced open. */
export function loadFromStorage(): void {
  try {
    const raw = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_KEY);
    if (!raw) { useCanvasStore.getState().load({ ...emptyState() }); return; }
    useCanvasStore.getState().load({ ...emptyState(), doc: decodeDoc(raw) });
  } catch (err) {
    console.warn('[codeframe] failed to load doc from localStorage', err);
  }
}

/** Apply a serialized doc (used by the sync controller's loadDoc). Guards the
 *  autosave subscribers so the load isn't echoed back as a user edit. */
export function applyDocJson(docJson: string): void {
  applyingDoc = true;
  try { useCanvasStore.getState().load({ ...emptyState(), doc: decodeDoc(docJson) }); }
  finally { applyingDoc = false; }
}

/** Autosave for the NO-project (legacy) case: persist to localStorage. When a
 *  project is active, the sync controller (driven from app.tsx) owns both the
 *  local cache and the server push, so this only broadcasts. Programmatic loads
 *  are skipped via the applyingDoc guard. */
export function startAutoSave(): () => void {
  return useCanvasStore.subscribe((s) => {
    if (applyingDoc) return;
    if (timer !== undefined) clearTimeout(timer);
    const doc = s.doc;
    timer = window.setTimeout(() => {
      const { projectId } = useCurrentProject.getState();
      if (!projectId) {
        try { localStorage.setItem(KEY, encodeDoc(doc)); } catch (err) { console.warn('[codeframe] localStorage quota or denied', err); }
      }
      broadcastDoc(doc);
    }, 400);
  });
}
